// Browser client for OpenAI GPT-Live (gpt-live-1) over WebRTC.
//
// Connection (GPT-Live WebRTC guide):
//   1. Mic tracks and the "oai-events" data channel go on an RTCPeerConnection before
//      the offer is created, so both are negotiated in the SDP.
//   2. The complete offer (after ICE gathering) goes to our server at /api/session. The
//      server adds the API key and session config and posts { session, transport } to
//      POST /v1/live/sessions. That HTTP call starts the session.
//   3. We apply the SDP answer and wait for session.started. Never send session.start.
//
// Game tools run here in the browser. GPT-Live delegates to a Responses backend that
// the server configured with LIVE_TOOLS; the backend's function calls arrive as nested
// `response.event` messages. DelegationLoop answers each call with a
// function_call_output item, then sends one response.create so the backend continues.
//
// Transcripts: GPT-Live only sends session.input_transcript.delta and
// session.output_transcript.delta (with start_ms/end_ms) and has no "segment done"
// event, so TranscriptSegmenter ends a segment after a timeline gap or a short silence.
//
// Callback contract: state callbacks fire only on real transitions, and none fire after
// stop() returns (a final onUsage report may still arrive once the session finalizes).

export type LiveState = 'idle' | 'connecting' | 'live' | 'error' | 'closed';

export interface LiveCallbacks {
  onState(state: LiveState, detail?: string): void;
  /** Text of the current user segment so far; final=true once the segment ends. */
  onUserTranscript(text: string, final: boolean): void;
  onAssistantTranscript(text: string, final: boolean): void;
  /** Runs a game tool. Resolve with the JSON string to send back as the call's output. */
  onToolCall(name: string, args: Record<string, unknown>): Promise<string>;
  /** Receives LiveUsageReport objects. */
  onUsage?(usage: unknown): void;
}

export interface LiveOptions {
  /** Defaults to '/api/session'. */
  sessionUrl?: string;
  /** Sent as the x-voice-pass header when the server requires a passphrase. */
  passphrase?: string;
}

/** What LiveCallbacks.onUsage receives. Voice is billed per second; backend tokens separately. */
export type LiveUsageReport =
  | { kind: 'voice'; seconds: number; contextUsageRatio?: number; final: boolean; reason?: string }
  | { kind: 'backend'; responseId: string; usage: unknown };

type Json = Record<string, unknown>;

const DATA_CHANNEL_LABEL = 'oai-events';
const ICE_GATHER_TIMEOUT_MS = 10_000;
const START_TIMEOUT_MS = 20_000;
const CLOSE_TIMEOUT_MS = 15_000;
const CONTEXT_INTERVAL_MS = 4_000;
const CONTEXT_MAX_CHARS = 1_500; // comfortably under the 500-token append limit
const CONTEXT_MAX_ENTRIES = 6;
const TOOL_TIMEOUT_MS = 15_000;
const CONTINUE_FALLBACK_MS = 3_000;
const DEFAULT_MAX_MESSAGE_BYTES = 65_536; // SCTP default when the peer doesn't say
const MESSAGE_OVERHEAD_BYTES = 512;
const GESTURE_EVENTS = ['pointerdown', 'keydown', 'touchend'] as const;

// ---------------------------------------------------------------------------
// Event ids

let eventSeq = 0;
const pageTag = Math.random().toString(36).slice(2, 8);

/** Unique client event id: a per-page counter plus a random page tag and timestamp. */
export function newEventId(prefix = 'evt'): string {
  eventSeq += 1;
  return `${prefix}_${pageTag}${Date.now().toString(36)}_${eventSeq.toString(36)}`;
}

// ---------------------------------------------------------------------------
// Tool-call loop (pure: no DOM, no WebRTC, so it can be tested with fake events)

export interface DelegationLoopOptions {
  /** Sends one client event on the data channel. Returns false if it couldn't be sent. */
  send(message: Json): boolean;
  runTool(name: string, args: Record<string, unknown>): Promise<string>;
  newId?(prefix: string): string;
  /** A tool slower than this gets an error output, so the backend never waits forever. */
  toolTimeoutMs?: number;
  /** If every call is answered but the response never reports an end, continue after this. */
  continueFallbackMs?: number;
  /** Byte budget for one serialized data-channel message. */
  maxMessageBytes?(): number;
  onBackendUsage?(responseId: string, usage: unknown): void;
}

interface TrackedResponse {
  key: string;
  /** call_id -> output sent */
  calls: Map<string, boolean>;
  ended: boolean;
  resumable: boolean;
  continued: boolean;
  continueId: string | null;
  fallback: ReturnType<typeof setTimeout> | null;
}

// Nested Responses lifecycle names. Live forwards response.completed; the others are
// accepted defensively.
const RESPONSE_STARTED = new Set(['response.created', 'response.queued', 'response.in_progress']);
const RESPONSE_ENDED_RESUMABLE = new Set(['response.completed', 'response.done', 'response.incomplete']);
const RESPONSE_ENDED_FINAL = new Set(['response.failed', 'response.cancelled', 'response.canceled']);

/**
 * Runs the backend's function calls and keeps the delegated response moving:
 * every call gets a `response.item.create` function_call_output (even when the tool
 * throws or its arguments are bad), and once all of a response's calls are answered and
 * the response has ended, exactly one `response.create` continues it.
 *
 * Nested output_item.done events carry no response id, so calls are filed under the
 * latest response seen for their delegation (from session.delegation.created or the
 * nested response.created). Lifecycle events carry the full response object.
 */
export class DelegationLoop {
  private readonly opts: DelegationLoopOptions;
  private readonly responses = new Map<string, TrackedResponse>();
  /** delegation_id -> latest response id */
  private readonly current = new Map<string, string>();
  private readonly seenCalls = new Set<string>();
  /** response.create event_id -> response, to recover if that command is rejected */
  private readonly continues = new Map<string, TrackedResponse>();
  private lastKey: string | null = null;
  private queue: Promise<void> = Promise.resolve();
  private stopped = false;

  constructor(opts: DelegationLoopOptions) {
    this.opts = opts;
  }

  /** session.delegation.created: remember the response a Responses delegation starts with. */
  handleDelegationCreated(event: Json): void {
    const delegation = isObj(event.delegation) ? event.delegation : null;
    if (!delegation || delegation.target !== 'responses') return;
    const delegationId = str(delegation.id);
    const responseId = str(delegation.response_id);
    if (delegationId && responseId) this.bind(delegationId, responseId);
  }

  /** One `response.event` envelope: { type: 'response.event', delegation_id, event }. */
  handleResponseEvent(envelope: Json): void {
    if (this.stopped) return;
    const event = isObj(envelope.event) ? envelope.event : null;
    if (!event) return;
    const type = str(event.type) ?? '';
    const delegationId = str(envelope.delegation_id);

    if (RESPONSE_STARTED.has(type)) {
      const id = responseIdOf(event);
      if (id) {
        if (delegationId) this.bind(delegationId, id);
        this.track(id);
        this.lastKey = id;
      }
      return;
    }
    if (type === 'response.output_item.done') {
      const item = isObj(event.item) ? event.item : null;
      if (item?.type === 'function_call') this.onFunctionCall(this.keyFor(event, delegationId), item);
      return;
    }
    if (RESPONSE_ENDED_RESUMABLE.has(type) || RESPONSE_ENDED_FINAL.has(type)) {
      // If no start event named this delegation's response, adopt calls filed under it.
      const id = responseIdOf(event);
      if (id && delegationId && !this.current.has(delegationId)) this.bind(delegationId, id);
      const key = this.keyFor(event, delegationId);
      const usage = isObj(event.response) ? event.response.usage : undefined;
      if (usage != null) this.opts.onBackendUsage?.(key, usage);
      this.onResponseEnded(key, RESPONSE_ENDED_RESUMABLE.has(type));
    }
    // Text and argument deltas, output_item.added and the rest need no action.
  }

  /** Called for an `error` event that names one of our commands by client_event_id. */
  handleCommandError(clientEventId: string): void {
    const r = this.continues.get(clientEventId);
    if (!r) return;
    this.continues.delete(clientEventId);
    r.continueId = null;
    // A fallback continue can race the response; try again when it actually ends.
    if (!r.ended) r.continued = false;
  }

  /** Stop running tools and sending (the session is closing). */
  stop(): void {
    this.stopped = true;
    for (const r of this.responses.values()) this.clearFallback(r);
  }

  /** Resolves when every tool call queued so far has run and been answered. */
  idle(): Promise<void> {
    return this.queue;
  }

  private bind(delegationId: string, responseId: string): void {
    const previous = this.current.get(delegationId);
    if (previous === responseId) return;
    this.current.set(delegationId, responseId);
    // Calls that arrived before the response id was known were filed under the delegation.
    const orphan = this.responses.get(delegationKey(delegationId));
    if (orphan && !this.responses.has(responseId)) {
      this.responses.delete(orphan.key);
      orphan.key = responseId;
      this.responses.set(responseId, orphan);
    }
    const old = previous ? this.responses.get(previous) : undefined;
    if (old && (old.continued || (old.ended && old.calls.size === 0))) this.forget(old);
  }

  private keyFor(event: Json, delegationId: string | null): string {
    const id = responseIdOf(event);
    if (id) return id;
    if (delegationId) return this.current.get(delegationId) ?? delegationKey(delegationId);
    return this.lastKey ?? 'unknown';
  }

  private track(key: string): TrackedResponse {
    let r = this.responses.get(key);
    if (!r) {
      r = { key, calls: new Map(), ended: false, resumable: false, continued: false, continueId: null, fallback: null };
      this.responses.set(key, r);
    }
    return r;
  }

  private forget(r: TrackedResponse): void {
    this.clearFallback(r);
    if (r.continueId) this.continues.delete(r.continueId);
    if (this.responses.get(r.key) === r) this.responses.delete(r.key);
  }

  private onFunctionCall(key: string, item: Json): void {
    const callId = str(item.call_id);
    const name = str(item.name);
    if (!callId || !name) {
      console.warn('[voice] function call without call_id or name', item);
      return;
    }
    if (this.seenCalls.has(callId)) return; // duplicate output_item.done
    this.seenCalls.add(callId);
    const r = this.track(key);
    r.calls.set(callId, false);
    const args = parseArguments(item.arguments);
    // One at a time, in the order the backend emitted them: game state changes stay
    // deterministic, and a `connect` can rely on the `build` emitted before it.
    this.queue = this.queue.then(() => this.runCall(r, callId, name, args));
  }

  private async runCall(r: TrackedResponse, callId: string, name: string, args: Record<string, unknown> | Error): Promise<void> {
    if (this.stopped) return;
    let output: string;
    if (args instanceof Error) {
      output = JSON.stringify({ ok: false, error: `Invalid arguments for ${name}: ${args.message}` });
    } else {
      try {
        const run = Promise.resolve().then(() => this.opts.runTool(name, args));
        output = normalizeOutput(await withTimeout(run, this.opts.toolTimeoutMs ?? TOOL_TIMEOUT_MS, name));
      } catch (err) {
        output = JSON.stringify({ ok: false, error: errorText(err) });
      }
    }
    if (this.stopped) return;
    output = fitOutput(output, this.opts.maxMessageBytes?.() ?? DEFAULT_MAX_MESSAGE_BYTES);
    if (!this.sendOutput(callId, output)) {
      this.sendOutput(callId, JSON.stringify({ ok: false, error: 'The tool result could not be delivered' }));
    }
    r.calls.set(callId, true);
    this.maybeContinue(r);
  }

  private sendOutput(callId: string, output: string): boolean {
    return this.opts.send({
      type: 'response.item.create',
      event_id: this.newId('tool'),
      item: { type: 'function_call_output', call_id: callId, output },
    });
  }

  private onResponseEnded(key: string, resumable: boolean): void {
    const r = this.track(key);
    r.ended = true;
    r.resumable = resumable;
    if (r.calls.size === 0) {
      this.forget(r); // a plain answer: nothing to continue
      return;
    }
    if (!resumable) console.warn(`[voice] backend response ${key} did not complete; not continuing it`);
    this.maybeContinue(r);
  }

  private maybeContinue(r: TrackedResponse): void {
    if (this.stopped || r.continued || r.calls.size === 0) return;
    for (const answered of r.calls.values()) if (!answered) return;
    if (!r.ended) {
      // response.completed normally follows the last call right away. If it never
      // arrives, continue anyway rather than leave the backend waiting.
      if (!r.fallback) {
        r.fallback = setTimeout(() => {
          r.fallback = null;
          if (this.stopped || r.ended || r.continued) return;
          console.warn(`[voice] no end event for backend response ${r.key}; continuing anyway`);
          this.sendContinue(r);
        }, this.opts.continueFallbackMs ?? CONTINUE_FALLBACK_MS);
      }
      return;
    }
    if (!r.resumable) {
      this.forget(r);
      return;
    }
    this.sendContinue(r);
  }

  private sendContinue(r: TrackedResponse): void {
    this.clearFallback(r);
    const eventId = this.newId('continue');
    if (!this.opts.send({ type: 'response.create', event_id: eventId })) return;
    r.continued = true;
    r.continueId = eventId;
    this.continues.set(eventId, r);
  }

  private clearFallback(r: TrackedResponse): void {
    if (r.fallback) clearTimeout(r.fallback);
    r.fallback = null;
  }

  private newId(prefix: string): string {
    return this.opts.newId?.(prefix) ?? newEventId(prefix);
  }
}

// ---------------------------------------------------------------------------
// Transcript segments

/**
 * Accumulates one speaker's transcript deltas into segments. GPT-Live has no
 * transcript-done event, so a segment ends when the timeline jumps by more than
 * `gapMs` between fragments, or when no fragment arrives for `idleMs`.
 */
export class TranscriptSegmenter {
  private readonly emit: (text: string, final: boolean) => void;
  private readonly gapMs: number;
  private readonly idleMs: number;
  private text = '';
  private lastEndMs = Number.NaN;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(emit: (text: string, final: boolean) => void, opts: { gapMs?: number; idleMs?: number } = {}) {
    this.emit = emit;
    this.gapMs = opts.gapMs ?? 900;
    this.idleMs = opts.idleMs ?? 1_200;
  }

  push(delta: unknown, startMs?: unknown, endMs?: unknown): void {
    if (typeof delta !== 'string' || delta === '') return;
    if (this.text && typeof startMs === 'number' && Number.isFinite(this.lastEndMs) && startMs - this.lastEndMs > this.gapMs) {
      this.flush();
    }
    this.text += delta; // fragments are appended exactly as received
    if (typeof endMs === 'number') this.lastEndMs = Number.isFinite(this.lastEndMs) ? Math.max(this.lastEndMs, endMs) : endMs;
    const shown = this.text.trim();
    if (shown) this.emit(shown, false);
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flush();
    }, this.idleMs);
  }

  /** Ends the open segment, if any, with a final emit. */
  flush(): void {
    const shown = this.text.trim();
    this.reset();
    if (shown) this.emit(shown, true);
  }

  /** Drops the open segment without emitting. */
  reset(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.text = '';
    this.lastEndMs = Number.NaN;
  }
}

// ---------------------------------------------------------------------------
// The client

export class LiveVoice {
  private readonly cb: LiveCallbacks;
  private readonly sessionUrl: string;
  private readonly passphrase: string;
  private readonly userSegment: TranscriptSegmenter;
  private readonly assistantSegment: TranscriptSegmenter;

  private state: LiveState = 'idle';
  /** Bumped by teardown(); handlers from an older connection check it and bail. */
  private conn = 0;
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private mic: MediaStream | null = null;
  private audio: HTMLAudioElement | null = null;
  private abort: AbortController | null = null;
  private loop: DelegationLoop | null = null;
  private started = false;
  private closing = false;
  private finalized = false;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private startWaiter: { resolve(): void; reject(err: Error): void } | null = null;
  private replayAudio: (() => void) | null = null;
  private pageHide: (() => void) | null = null;
  private context: string[] = [];
  private contextTimer: ReturnType<typeof setTimeout> | null = null;
  private contextSentAt = Number.NEGATIVE_INFINITY;
  private contextLast = '';
  private id: string | null = null;

  constructor(cb: LiveCallbacks, opts: LiveOptions = {}) {
    this.cb = cb;
    this.sessionUrl = opts.sessionUrl ?? '/api/session';
    this.passphrase = opts.passphrase ?? '';
    this.userSegment = new TranscriptSegmenter((text, final) => this.safely(() => this.cb.onUserTranscript(text, final)));
    this.assistantSegment = new TranscriptSegmenter((text, final) => this.safely(() => this.cb.onAssistantTranscript(text, final)));
  }

  /** True while connecting or live. False as soon as stop() is called. */
  get active(): boolean {
    return this.state === 'connecting' || this.state === 'live';
  }

  /** GPT-Live session id, once known. */
  get sessionId(): string | null {
    return this.id;
  }

  /**
   * Opens the mic and connects. Call from a user gesture. Never rejects: failures are
   * reported through onState('error', message).
   */
  async start(): Promise<void> {
    if (this.active) return;
    this.teardown(); // a previous session may still be finalizing; drop it
    const conn = ++this.conn;
    this.setState('connecting');
    const abort = new AbortController();
    this.abort = abort;
    try {
      if (typeof RTCPeerConnection === 'undefined') throw new Error('This browser does not support WebRTC voice');
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        throw new Error('Voice needs HTTPS or localhost for microphone access');
      }
      this.ensureAudio().muted = false;

      const mic = await openMicrophone();
      if (conn !== this.conn) {
        stopTracks(mic);
        return;
      }
      this.mic = mic;

      const pc = new RTCPeerConnection();
      this.pc = pc;
      pc.addEventListener('track', (e: RTCTrackEvent) => {
        if (conn === this.conn) this.playRemote(e);
      });
      pc.addEventListener('connectionstatechange', () => {
        if (conn === this.conn && pc.connectionState === 'failed' && !this.closing) this.fail('Voice connection failed');
      });
      for (const track of mic.getAudioTracks()) pc.addTrack(track, mic);

      // The event channel must exist before the offer so it is part of the negotiation.
      const dc = pc.createDataChannel(DATA_CHANNEL_LABEL);
      this.dc = dc;
      dc.addEventListener('message', (e: MessageEvent) => {
        if (conn === this.conn) this.onMessage(e.data);
      });
      dc.addEventListener('close', () => {
        if (conn === this.conn) this.onChannelClosed();
      });

      this.loop = new DelegationLoop({
        send: (message) => this.send(message),
        runTool: (name, args) => this.cb.onToolCall(name, args),
        maxMessageBytes: () => this.maxMessageBytes(),
        onBackendUsage: (responseId, usage) => this.emitUsage({ kind: 'backend', responseId, usage }),
      });

      await pc.setLocalDescription(await pc.createOffer());
      await waitForIceGathering(pc, ICE_GATHER_TIMEOUT_MS);
      if (conn !== this.conn) return;
      const offer = pc.localDescription?.sdp;
      if (!offer) throw new Error('Could not create a voice connection offer');
      if (!offer.includes('a=candidate')) throw new Error('Could not find a network path for voice');

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // URI-encoded so any passphrase survives as a header; the server decodes it.
      if (this.passphrase) headers['x-voice-pass'] = encodeURIComponent(this.passphrase);
      const res = await fetch(this.sessionUrl, { method: 'POST', headers, body: JSON.stringify({ sdp: offer }), signal: abort.signal });
      const body = await readJson(res);
      if (conn !== this.conn) return;
      if (!res.ok) throw new Error(sessionError(res.status, body));
      const answer = isObj(body) && isObj(body.transport) ? str(body.transport.sdp) : null;
      if (!answer) throw new Error('The voice server returned no SDP answer');
      this.id = isObj(body) && isObj(body.session) ? str(body.session.id) : null;

      // The HTTP request started the session. Do not send session.start.
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });
      if (conn !== this.conn) return;
      await this.waitForStart(START_TIMEOUT_MS);
    } catch (err) {
      if (conn !== this.conn || this.state !== 'connecting') return; // stopped, superseded or already reported
      this.fail(startErrorText(err));
    }
  }

  /**
   * Ends the conversation. When live, sends session.close and keeps the connection up
   * until session.closed reports final usage (at most 15 s), with the mic and speaker
   * muted meanwhile. Reports onState('closed') immediately.
   */
  stop(): void {
    if (!this.active) return;
    this.flushTranscripts();
    this.loop?.stop(); // a response waiting on tool results can't continue once closing starts
    this.clearContext();
    if (this.state === 'live' && this.started && this.dc?.readyState === 'open') {
      this.closing = true;
      for (const track of this.mic?.getAudioTracks() ?? []) track.enabled = false;
      if (this.audio) this.audio.muted = true;
      this.send({ type: 'session.close', event_id: newEventId('close') });
      this.closeTimer = setTimeout(() => {
        console.warn('[voice] no session.closed within 15 s; final usage unconfirmed');
        this.teardown();
      }, CLOSE_TIMEOUT_MS);
    } else {
      this.teardown();
    }
    this.setState('closed');
  }

  /**
   * Quiet app context for the voice model (session.thinking.append, delegation_id null).
   * At most one append per 4 s: extras merge into the next one, unchanged text is
   * skipped, and the content is capped at ~1500 characters (oldest entries drop first).
   */
  sendContext(text: string): void {
    const clean = typeof text === 'string' ? text.trim() : '';
    if (!clean || !this.active || this.closing) return;
    if (!this.context.includes(clean)) {
      this.context.push(clean);
      if (this.context.length > CONTEXT_MAX_ENTRIES) this.context.splice(0, this.context.length - CONTEXT_MAX_ENTRIES);
    }
    this.pumpContext();
  }

  // --- events -------------------------------------------------------------

  private onMessage(data: unknown): void {
    let event: Json;
    try {
      const parsed: unknown = JSON.parse(typeof data === 'string' ? data : String(data));
      if (!isObj(parsed)) return;
      event = parsed;
    } catch {
      console.warn('[voice] unreadable data channel message');
      return;
    }
    const type = str(event.type) ?? '';
    switch (type) {
      case 'session.started':
        this.onStarted(event);
        break;
      case 'session.input_transcript.delta':
        if (!this.closing) this.userSegment.push(event.delta, event.start_ms, event.end_ms);
        break;
      case 'session.output_transcript.delta':
        if (!this.closing) this.assistantSegment.push(event.delta, event.start_ms, event.end_ms);
        break;
      case 'session.delegation.created':
        this.loop?.handleDelegationCreated(event);
        break;
      case 'response.event':
        this.loop?.handleResponseEvent(event);
        break;
      case 'session.usage.updated':
        this.onUsageUpdated(event);
        break;
      case 'session.closed':
        this.onSessionClosed(event);
        break;
      case 'error':
        this.onServerError(event);
        break;
      default:
        // session.thinking.appended, info (e.g. data_channel_permissions), etc.
        console.debug('[voice] event', type, event);
    }
  }

  private onStarted(event: Json): void {
    this.started = true; // before setState: onState('live') handlers may call sendContext
    const session = isObj(event.session) ? event.session : null;
    this.id = str(session?.id) ?? this.id;
    this.addPageHide();
    if (this.state === 'connecting') this.setState('live');
    this.startWaiter?.resolve();
    this.pumpContext();
  }

  private onUsageUpdated(event: Json): void {
    const seconds = num(isObj(event.usage) ? event.usage.seconds : undefined);
    if (seconds === undefined) return;
    const ratio = num(isObj(event.context_window) ? event.context_window.usage_ratio : undefined);
    this.emitUsage({ kind: 'voice', seconds, contextUsageRatio: ratio, final: false });
  }

  private onSessionClosed(event: Json): void {
    this.finalized = true;
    const reason = str(event.reason) ?? 'unknown';
    const seconds = num(isObj(event.usage) ? event.usage.seconds : undefined);
    if (seconds !== undefined) this.emitUsage({ kind: 'voice', seconds, final: true, reason });
    const requested = this.closing;
    this.flushTranscripts();
    this.teardown();
    if (requested) return; // stop() already reported 'closed'
    const [state, detail] = closeOutcome(reason);
    this.setState(state, detail);
  }

  private onServerError(event: Json): void {
    const error = isObj(event.error) ? event.error : {};
    const message = str(error.message) ?? 'GPT-Live reported an error';
    const clientEventId = str(error.client_event_id) ?? str(event.client_event_id);
    if (clientEventId) this.loop?.handleCommandError(clientEventId);
    // Before session.started nothing works yet, so any error is fatal. After that an
    // error rejects one command or cuts one reply short; a session that really ends
    // sends session.closed or drops the channel, and those are handled separately.
    if (!this.started) {
      this.fail(message);
      return;
    }
    console.warn('[voice] GPT-Live error:', str(error.code) ?? str(error.type) ?? '', message, clientEventId ? `(command ${clientEventId})` : '');
  }

  private onChannelClosed(): void {
    if (this.finalized) return;
    if (this.closing) {
      console.warn('[voice] channel closed before session.closed; final usage unconfirmed');
      this.teardown();
      return;
    }
    if (this.active) this.fail('Voice connection dropped');
  }

  // --- helpers ------------------------------------------------------------

  private pumpContext(): void {
    if (!this.started || this.closing || this.contextTimer || this.context.length === 0) return;
    const wait = this.contextSentAt + CONTEXT_INTERVAL_MS - Date.now();
    if (wait > 0) {
      this.contextTimer = setTimeout(() => {
        this.contextTimer = null;
        this.pumpContext();
      }, wait);
      return;
    }
    const content = joinContext(this.context);
    this.context = [];
    if (content === this.contextLast) return; // nothing changed since the last update
    if (this.send({ type: 'session.thinking.append', event_id: newEventId('ctx'), delegation_id: null, content })) {
      this.contextSentAt = Date.now();
      this.contextLast = content;
    }
  }

  private clearContext(): void {
    if (this.contextTimer) clearTimeout(this.contextTimer);
    this.contextTimer = null;
    this.context = [];
    this.contextSentAt = Number.NEGATIVE_INFINITY;
    this.contextLast = '';
  }

  private send(message: Json): boolean {
    const dc = this.dc;
    if (!dc || dc.readyState !== 'open') {
      console.warn('[voice] not connected; dropped', message.type);
      return false;
    }
    try {
      dc.send(JSON.stringify(message));
      return true;
    } catch (err) {
      console.warn('[voice] could not send', message.type, err);
      return false;
    }
  }

  private maxMessageBytes(): number {
    const max = this.pc?.sctp?.maxMessageSize;
    return typeof max === 'number' && Number.isFinite(max) && max > 0 ? Math.min(max, 262_144) : DEFAULT_MAX_MESSAGE_BYTES;
  }

  private waitForStart(ms: number): Promise<void> {
    if (this.started) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.startWaiter = null;
        reject(new Error('GPT-Live did not start in time'));
      }, ms);
      this.startWaiter = {
        resolve: () => {
          clearTimeout(timer);
          this.startWaiter = null;
          resolve();
        },
        reject: (err) => {
          clearTimeout(timer);
          this.startWaiter = null;
          reject(err);
        },
      };
    });
  }

  private ensureAudio(): HTMLAudioElement {
    if (this.audio) return this.audio;
    const el = document.createElement('audio');
    el.autoplay = true;
    el.setAttribute('playsinline', '');
    el.setAttribute('aria-hidden', 'true');
    el.style.display = 'none';
    document.body.appendChild(el);
    this.audio = el;
    return el;
  }

  private playRemote(event: RTCTrackEvent): void {
    const audio = this.audio;
    if (!audio) return;
    const stream = event.streams[0] ?? new MediaStream([event.track]);
    if (audio.srcObject !== stream) audio.srcObject = stream;
    this.tryPlay();
  }

  private tryPlay(): void {
    const audio = this.audio;
    if (!audio || !audio.srcObject) return;
    let attempt: Promise<void> | undefined;
    try {
      attempt = audio.play();
    } catch (err) {
      this.audioBlocked(err);
      return;
    }
    attempt?.then(
      () => this.removeReplay(),
      (err: unknown) => this.audioBlocked(err),
    );
  }

  /** Autoplay was refused: retry on the next tap or key press (players do both constantly). */
  private audioBlocked(err: unknown): void {
    if (errorName(err) === 'AbortError' || !this.active || this.replayAudio) return;
    console.warn('[voice] browser blocked audio playback; retrying on the next tap or key press');
    const replay = () => {
      this.removeReplay();
      this.tryPlay();
    };
    this.replayAudio = replay;
    for (const type of GESTURE_EVENTS) window.addEventListener(type, replay, true);
  }

  private removeReplay(): void {
    const replay = this.replayAudio;
    if (!replay) return;
    for (const type of GESTURE_EVENTS) window.removeEventListener(type, replay, true);
    this.replayAudio = null;
  }

  /** Closing the tab would otherwise leave the session billing until the server notices. */
  private addPageHide(): void {
    if (this.pageHide || typeof window === 'undefined') return;
    const onHide = () => {
      if (this.started && !this.closing) this.send({ type: 'session.close', event_id: newEventId('close') });
    };
    this.pageHide = onHide;
    window.addEventListener('pagehide', onHide);
  }

  private removePageHide(): void {
    if (this.pageHide) window.removeEventListener('pagehide', this.pageHide);
    this.pageHide = null;
  }

  private flushTranscripts(): void {
    this.userSegment.flush();
    this.assistantSegment.flush();
  }

  private fail(message: string): void {
    this.flushTranscripts();
    this.teardown();
    this.setState('error', message);
  }

  /** Releases everything for the current connection. Never reports state. */
  private teardown(): void {
    this.conn++;
    this.abort?.abort();
    this.abort = null;
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = null;
    this.startWaiter?.reject(new Error('Voice session closed'));
    this.loop?.stop();
    this.loop = null;
    this.clearContext();
    this.userSegment.reset();
    this.assistantSegment.reset();
    this.removeReplay();
    this.removePageHide();
    try {
      this.dc?.close();
    } catch {
      // already closed
    }
    try {
      this.pc?.close();
    } catch {
      // already closed
    }
    stopTracks(this.mic);
    if (this.audio) this.audio.srcObject = null;
    this.dc = null;
    this.pc = null;
    this.mic = null;
    this.started = false;
    this.closing = false;
    this.finalized = false;
  }

  private setState(state: LiveState, detail?: string): void {
    this.state = state;
    this.safely(() => this.cb.onState(state, detail));
  }

  private emitUsage(report: LiveUsageReport): void {
    if (this.cb.onUsage) this.safely(() => this.cb.onUsage?.(report));
  }

  private safely(fn: () => void): void {
    try {
      fn();
    } catch (err) {
      console.error('[voice] callback threw', err);
    }
  }
}

/**
 * GET /api/voice-status. Also reports gptLive:false when this browser can't run WebRTC
 * voice (no RTCPeerConnection, or no microphone API outside HTTPS/localhost). Never
 * throws: network and HTTP errors come back as { gptLive: false, reason }.
 */
export async function voiceStatus(url = '/api/voice-status'): Promise<{ gptLive: boolean; reason?: string; passphrase?: boolean }> {
  if (typeof RTCPeerConnection === 'undefined') return { gptLive: false, reason: 'This browser does not support WebRTC voice' };
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return { gptLive: false, reason: 'Voice needs HTTPS or localhost for microphone access' };
  }
  try {
    const res = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!res.ok) return { gptLive: false, reason: `Voice status unavailable (HTTP ${res.status})` };
    const body: unknown = await res.json();
    if (!isObj(body)) return { gptLive: false, reason: 'Voice status unreadable' };
    const reason = str(body.reason);
    return { gptLive: body.gptLive === true, ...(reason ? { reason } : {}), passphrase: body.passphrase === true };
  } catch (err) {
    return { gptLive: false, reason: `Voice status check failed: ${errorText(err)}` };
  }
}

// ---------------------------------------------------------------------------
// Utilities

function isObj(v: unknown): v is Json {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v !== '' ? v : null;
}

function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function errorName(err: unknown): string {
  return isObj(err) || err instanceof Error ? String((err as { name?: unknown }).name ?? '') : '';
}

function errorText(err: unknown): string {
  if (err instanceof Error) return err.message || err.name;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

function responseIdOf(event: Json): string | null {
  return (isObj(event.response) ? str(event.response.id) : null) ?? str(event.response_id);
}

function delegationKey(delegationId: string): string {
  return `delegation:${delegationId}`;
}

function parseArguments(raw: unknown): Record<string, unknown> | Error {
  if (raw === undefined || raw === null || raw === '') return {};
  if (isObj(raw)) return raw;
  if (typeof raw !== 'string') return new Error('arguments must be a JSON object');
  try {
    const value: unknown = JSON.parse(raw);
    return isObj(value) ? value : new Error('arguments must be a JSON object');
  } catch (err) {
    return new Error(`arguments are not valid JSON (${errorText(err)})`);
  }
}

function normalizeOutput(value: unknown): string {
  if (typeof value === 'string') return value.trim() ? value : '{"ok":true}';
  if (value === undefined || value === null) return '{"ok":true}';
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function jsonBytes(text: string): number {
  return new TextEncoder().encode(JSON.stringify(text)).byteLength;
}

/** Truncates a tool output so its data-channel message fits the negotiated size limit. */
function fitOutput(output: string, maxBytes: number): string {
  const budget = Math.max(1_024, maxBytes - MESSAGE_OVERHEAD_BYTES);
  if (jsonBytes(output) <= budget) return output;
  console.warn('[voice] tool output too large for the data channel; truncating it');
  const marker = ' ...[truncated: tool output too large]';
  let keep = output.length;
  let candidate = output;
  while (keep > 0 && jsonBytes(candidate) > budget) {
    keep = Math.floor(keep * Math.min(0.9, budget / jsonBytes(candidate)));
    candidate = output.slice(0, keep) + marker;
  }
  return candidate;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)} s`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function joinContext(entries: string[]): string {
  const parts = [...entries];
  let text = parts.join('\n');
  while (text.length > CONTEXT_MAX_CHARS && parts.length > 1) {
    parts.shift();
    text = parts.join('\n');
  }
  return text.length > CONTEXT_MAX_CHARS ? `${text.slice(0, CONTEXT_MAX_CHARS - 1)}…` : text;
}

function closeOutcome(reason: string): [LiveState, string | undefined] {
  switch (reason) {
    case 'close_requested':
      return ['closed', undefined];
    case 'remote_hangup':
      return ['closed', 'Voice session ended'];
    case 'expired':
      return ['closed', 'Voice session reached its time limit'];
    case 'content':
      return ['error', 'Voice session was ended by a safety filter'];
    case 'connection_lost':
      return ['error', 'Voice connection lost'];
    default:
      return ['closed', `Voice session ended (${reason})`];
  }
}

async function openMicrophone(): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (err) {
    const name = errorName(err);
    if (name === 'NotAllowedError' || name === 'SecurityError') throw new Error('Microphone permission denied');
    if (name === 'NotFoundError' || name === 'OverconstrainedError') throw new Error('No microphone found');
    if (name === 'NotReadableError') throw new Error('The microphone is busy in another app');
    throw err;
  }
}

function stopTracks(stream: MediaStream | null): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

/** Waits for ICE gathering to finish. On timeout, goes ahead with the candidates gathered so far. */
function waitForIceGathering(pc: RTCPeerConnection, ms: number): Promise<void> {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise<void>((resolve) => {
    const finish = () => {
      clearTimeout(timer);
      pc.removeEventListener('icegatheringstatechange', check);
      resolve();
    };
    const check = () => {
      if (pc.iceGatheringState === 'complete') finish();
    };
    const timer = setTimeout(finish, ms);
    pc.addEventListener('icegatheringstatechange', check);
  });
}

async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

function sessionError(status: number, body: unknown): string {
  const message = isObj(body) ? str(body.error) : null;
  if (message) return message;
  if (status === 401) return 'Wrong or missing voice passphrase';
  if (status === 404) return 'The voice route /api/session is missing on this server';
  return `Voice server error (HTTP ${status})`;
}

function startErrorText(err: unknown): string {
  if (err instanceof TypeError && /fetch|network|load failed/i.test(err.message)) return 'Could not reach the voice server';
  return errorText(err);
}
